import { Inject } from '@odinjs/odin';
import { FieldAPI, FormAPI, ModalService, TextAreaAPI, ToastNotificationAPI } from '@philips-internal/oswald-ui-components';
import Controller, { OnBeforeSubmit, OnCancel, OnChange, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { AbortableSubmitEvent, ChangeEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { BrokerService, HttpService, MessageService, } from '@philips-internal/oswald-ui-core/lib/services';
import { FragmentNames, PatientQuestionsModalAliases } from '../../codegen';
import { BS_TASK_LIST_CURRENT_WIZARD_STEP, CTX_KEY_TASK_LIST_FIRST_STEP_RENDERED_NOT_DONE } from '../../constants/application-constants';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { APPLICATION_ROUTES } from '../../constants/application-routes';
import TASK_STATUS from '../../constants/task-status';
import TaskItem from '../../interfaces/task-item';
import { TaskModuleUtils } from '../../utils/task-module-utils';
import { TasksUtils } from '../../utils/tasks-utils';
import { showToastNotificationError } from '../../utils/toast-notification-utils';
import UserContext from '../../utils/user-context';
import BaseController from '../BaseController';
import { DYNAMIC_STEP_IDENTIFIER } from '../tasks/tasks-controller';

interface Questions {
  question01: string;
  question02: string;
  question03: string;
}

export const TASK_TYPE = {
  PM: 'PM'
}

enum ContextKeys {
  CURRENT_ROUTE_IS_TASKS = 'current_route_is_tasks',
  ASK_THE_PHYSICIAN_TASK = 'ask_the_physician_task',
}

enum ValidatorsUsed {
  MIN_LENGTH = 'min-length',
}

@Controller({ component: PatientQuestionsModalAliases.PATIENT_QUESTIONS_LAYOUT, fragment: FragmentNames.PATIENT_QUESTIONS_MODAL  })
export default class PatientQuestionsModalController extends BaseController {

  @Inject(PatientQuestionsModalAliases.FORM)
    patientQuestionFormApi!: FormAPI;

  @Inject(PatientQuestionsModalAliases.QUESTION_1)
    questionOneFieldApi!: FieldAPI;

  @Inject(PatientQuestionsModalAliases.QUESTION_2)
    questionTwoFieldApi!: FieldAPI;

  @Inject(PatientQuestionsModalAliases.QUESTION_3)
    questionThreeFieldApi!: FieldAPI;

  @Inject(PatientQuestionsModalAliases.QUESTION_01_TEXT_AREA)
    questionOneTextAreaApi!: TextAreaAPI;

  @Inject(PatientQuestionsModalAliases.QUESTION_02_TEXT_AREA)
    questionTwoTextAreaApi!: TextAreaAPI;

  @Inject(PatientQuestionsModalAliases.QUESTION_03_TEXT_AREA)
    questionThreeTextAreaApi!: TextAreaAPI;

  @Inject(PatientQuestionsModalAliases.TOAST_NOTIFICATION)
    toastNotificationBlockMessageApi!: ToastNotificationAPI;

  private MIN_QUESTION_LENGTH = 20;

  setQuestionFormInitialActions(): void {
    const isTasksRoute = this.context.get<boolean>(ContextKeys.CURRENT_ROUTE_IS_TASKS);

    if (isTasksRoute) {
      const askThePhysianTask = this.context.get<TaskItem>(ContextKeys.ASK_THE_PHYSICIAN_TASK);
      TasksUtils.setInitialFormStatus({
        formApi: this.patientQuestionFormApi,
        onBlocked: () => this.toastNotificationBlockMessageApi.setVisible(true),
        taskItem: askThePhysianTask
      });
    }
  }

  @OnMount({target: PatientQuestionsModalAliases.PATIENT_QUESTIONS_LAYOUT})
  onMountLayout():void {
    let nrSeqPrepPacient: string;
    this.setContextVariablesForTaskModule();
    const isTasksRoute = this.context.get<boolean>(ContextKeys.CURRENT_ROUTE_IS_TASKS);

    if (!isTasksRoute){
      // Required line, because by default the form is disabled because it is also used within the task fragment.
      this.disableForm(false);

      const nrSeqAgenda = (this.context.get('nrSeqAgenda') || null);
      HttpService.get(APPLICATION_ENDPOINTS.GET_TASK_LIST,
        { nrSeqAgenda, patientId: UserContext.patientId })
        .then(res=>{
          const tasks = res.body as TaskItem[];
          const askThePhysianTask = tasks.find(task => task.ieTipoTarefa === TASK_TYPE.PM);
          nrSeqPrepPacient = askThePhysianTask?.nrSequencia as unknown as string;
          this.retrievePatientQuestions(nrSeqPrepPacient);
          if (askThePhysianTask?.ieStatusTarefa !== TASK_STATUS.COMPLETED) {
            this.context.set({'nrSequencia': nrSeqPrepPacient});
          } else {
            this.disableForm(true);
          }
        });
    } else {
      const identifier = this.context.get('identifier') as string;
      nrSeqPrepPacient = identifier.split(DYNAMIC_STEP_IDENTIFIER.ASK_THE_PHYSICIAN.concat('-'))[1];
      this.retrievePatientQuestions(nrSeqPrepPacient);
      const taskList = this.context.get('taskList') as TaskItem[];
      const askThePhysianTask = taskList.find(task => task.nrSequencia === Number(nrSeqPrepPacient));
      this.context.set({[ContextKeys.ASK_THE_PHYSICIAN_TASK]: askThePhysianTask});

      this.listenerCurrentWizardStepFromParent(this.patientQuestionFormApi);
      const initialStepIdNotDone = this.context.get(CTX_KEY_TASK_LIST_FIRST_STEP_RENDERED_NOT_DONE);

      // Before publishing BS_TASK_LIST_CURRENT_WIZARD_STEP, ASK_THE_PHYSICIAN_TASK must already exist in the context.
      BrokerService.publishToTopic(BS_TASK_LIST_CURRENT_WIZARD_STEP, initialStepIdNotDone);

      if (askThePhysianTask?.ieStatusTarefa !== TASK_STATUS.COMPLETED) {
        this.context.set({'nrSequencia': nrSeqPrepPacient});
      } else {
        this.disableForm(true);
      }
    }

    this.setQuestionFormInitialActions();
  }

  private listenerCurrentWizardStepFromParent(formApi: FormAPI): void {
    const fragmentStepId = this.context.get<string>('identifier');
    const askThePhysianTask = this.context.get<TaskItem>(ContextKeys.ASK_THE_PHYSICIAN_TASK);
    TaskModuleUtils.listenerCurrentWizardStepFromParent(formApi, fragmentStepId, askThePhysianTask);
  }

  private setContextVariablesForTaskModule(): void {
    /**
     * This fragment is embedded within the task module.
     * When accessing the fragment via task mode, some different behaviors must be performed.
     * And for this needed this information in context.
     */
    const currentRouteIsTasks = this.historyService.getCurrentRoute() === APPLICATION_ROUTES.TASKS;
    this.context.set({[ContextKeys.CURRENT_ROUTE_IS_TASKS]: currentRouteIsTasks});
  }

  private retrievePatientQuestions(nrSeqPrepPacient: string):void{
    HttpService.get(APPLICATION_ENDPOINTS.RETRIEVE_PATIENT_QUESTIONS,
      { nrSeqPrepPacient })
      .then(res=>{
        if (!res?.body) {
          return;
        }
        const questions = res.body as Questions;
        this.questionOneTextAreaApi.setValue(questions.question01 || '');
        this.questionTwoTextAreaApi.setValue(questions.question02 || '');
        this.questionThreeTextAreaApi.setValue(questions.question03 || '');
      });
  }

  private disableForm(value: boolean): void{
    this.patientQuestionFormApi.setDisabled(value);
    this.patientQuestionFormApi.setEditing(!value);
  }

  @OnBeforeSubmit(PatientQuestionsModalAliases.FORM)
  onClickSendButton(event: AbortableSubmitEvent): void {
    const questions = event.record as Questions;
    const hasQuestions = Object.keys(questions).some(questKey => !!questions[questKey as keyof Questions]);
    if (!hasQuestions) {
      event.abort();
      return;
    }

    const isTasksRoute = this.historyService.getCurrentRoute() === APPLICATION_ROUTES.TASKS;
    const patientId = UserContext.patientId;
    const identifier = this.context.get('identifier') as string;

    const nrSeqPrepPacient = isTasksRoute ? identifier.split(DYNAMIC_STEP_IDENTIFIER.ASK_THE_PHYSICIAN.concat('-'))[1] : this.context.get('nrSequencia');

    const body = {...questions,
      nrSeqPrepPacient
    };

    HttpService.post(APPLICATION_ENDPOINTS.POST_PATIENT_QUESTIONS,
      { patientId }, { body })
      .then(()=>{
        this.disableForm(true);
        this.updateStatus(identifier, isTasksRoute);
      }).catch(() => {
        showToastNotificationError('newaccount.error.internal.server.error.try.again');
      });
  }

  private updateStatus(identifier: string, isTasksRoute: boolean):void{
    const nrSequencia = isTasksRoute ? identifier.split(DYNAMIC_STEP_IDENTIFIER.ASK_THE_PHYSICIAN.concat('-'))[1] : this.context.get('nrSequencia');
    HttpService.put(APPLICATION_ENDPOINTS.UPDATE_TASK_STATUS, { nrSequencia }).then(() => {
      BrokerService.publishToTopic(identifier, true);
      const askThePhysianTask = this.context.get<TaskItem>(ContextKeys.ASK_THE_PHYSICIAN_TASK);
      this.updateTaskStatusOnContext(askThePhysianTask);

      if (!isTasksRoute) {
        ModalService.close('patient-questions-modal');
      }
    });
  }

  private updateTaskStatusOnContext(askThePhysianTask: TaskItem): void {
    const isTasksRoute = this.context.get<boolean>(ContextKeys.CURRENT_ROUTE_IS_TASKS);
    if (isTasksRoute) {
      askThePhysianTask.ieStatusTarefa = TASK_STATUS.COMPLETED;
      this.context.set({ [ContextKeys.ASK_THE_PHYSICIAN_TASK]: askThePhysianTask });
    }
  }

  @OnCancel(PatientQuestionsModalAliases.FORM)
  onClickCancelButton(): void {
    ModalService.close('patient-questions-modal');
  }

  @OnMount([
    PatientQuestionsModalAliases.QUESTION_1,
    PatientQuestionsModalAliases.QUESTION_2,
    PatientQuestionsModalAliases.QUESTION_3
  ])
  onMountQuestionFields(event: MountEvent): void {
    const fieldApi = event.target as FieldAPI;
    fieldApi.setHelperText(MessageService.translate('patient.input.min-length', { minCaracter: this.MIN_QUESTION_LENGTH }));
  }

  @OnChange([
    PatientQuestionsModalAliases.QUESTION_1,
    PatientQuestionsModalAliases.QUESTION_2,
    PatientQuestionsModalAliases.QUESTION_3
  ])
  onChangeQuestionFields({ value, target }: ChangeEvent<string>): void {
    const fieldApi = target as FieldAPI;
    this.addOrRemoveLengthValidatorToField(value, fieldApi);
  }

  private addOrRemoveLengthValidatorToField(currentValue: string, fieldApi: FieldAPI): void {
    const hasValue = currentValue?.trim().length > 0 ? true : false;
    const hasMinLengthValidator = !!fieldApi.getValidators().find(validator => validator.name === ValidatorsUsed.MIN_LENGTH);

    if (hasValue && !hasMinLengthValidator) {
      fieldApi.addValidator({ name: ValidatorsUsed.MIN_LENGTH, options: { min: this.MIN_QUESTION_LENGTH }});
    } else if (!hasValue && hasMinLengthValidator) {
      fieldApi.removeValidator(ValidatorsUsed.MIN_LENGTH);
    }
  }
}